import { request } from '@/http/request'

/**
 * file 模块
 */

/**
 * @description 文件基础信息
 * @property {string} fileName 文件名称
 * @property {number | string} fileSize 文件大小（接口可能返回数字或字符串）
 * @property {string} contentType 文件类型
 * @property {string} [createUsername] 创建用户名称
 */
export interface FileBase {
    fileName: string
    fileSize: number | string
    contentType: string
    createUsername?: string
}

/** 文件详情中的存储记录 */
export interface FileStorageRecord {
    /** 文件下载地址 */
    fileUri?: string
    /** 存储类型 */
    storageType?: string | number
    /** 存储标识 */
    storageIdentity?: string
}

/**
 * @description 文件完整信息（列表/详情/上传返回字段与后端对齐）
 */
export interface File extends FileBase, StatusFlags {
    fileRecordId: string
    /** 备注 */
    comment?: string | null
    /** 创建用户 ID */
    createUser?: string
    /** 创建时间 */
    createTime?: string
    /** 删除时间 */
    deleteTime?: string | null
    /** 存储记录（详情接口返回） */
    storageRecords?: FileStorageRecord[]
}

/**
 * @description 文件列表分页查询参数
 * @property {number} [pageNum=1] 当前页码
 * @property {number} [pageSize=10] 每页条数
 * @property {string} [fileRecordId] 文件记录ID
 * @property {string} [fileName] 文件名称
 * @property {string} [createUser] 创建用户
 * @property {Builtin} [builtin] 是否内置
 * @property {string} [contentType] 文件类型
 */
export interface FileListPageReq extends PageReq {
    fileRecordId?: string
    fileName?: string
    createUser?: string
    builtin?: Builtin
    contentType?: string
}

// ==================== 文件列表接口（分页） ====================
/** @description 文件列表分页返回参数 */
export type FileListPageRes = PageRes<File>

/**
 * @description 分页查询文件列表
 * @param {FileListPageReq} params 分页查询参数
 * @returns {Promise<Res<FileListPageRes>>} 文件分页列表
 */
export const filePageList = (params: FileListPageReq) => {
    return request.get<Res<FileListPageRes>>({
        url: '/files',
        params,
        needPremission: true
    })
}

// ==================== 文件详情 ====================
/** @description 文件详情响应 */
export type FileDetailRes = File

/**
 * @description 获取文件详情
 * @param {string} fileRecordId 文件记录ID
 * @returns {Promise<Res<FileDetailRes>>} 文件详情
 */
export const fileDetail = (fileRecordId: string) => {
    return request.get<Res<FileDetailRes>>({
        url: `/files/${fileRecordId}`,
        needPremission: true
    })
}

// ==================== 文件上传 ====================
/**
 * @description 文件上传参数
 * @property {globalThis.File} file 上传文件（multipart 字段名 file）
 */
export interface FileUploadReq {
    file: globalThis.File
}

/**
 * @description 上传文件
 * @param {FileUploadReq} params 上传参数（含 file）
 * @returns {Promise<Res<File>>} 上传成功后的文件记录
 */
export const fileUpload = (params: FileUploadReq) => {
    return request.post<Res<File>>({
        url: '/files',
        data: params,
        needPremission: true,
        needLoading: true,
        timeout: 300000,
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
}

// ==================== 文件删除 ====================
/** @description 删除文件响应 */
export type FileDeleteRes = string

/** @description 批量删除文件响应 */
export type FileBatchDeleteRes = string[]

/**
 * @description 删除文件
 * @param {string} fileRecordId 文件记录ID
 * @returns {Promise<Res<FileDeleteRes>>} 删除成功的文件记录ID
 */
export const fileDelete = (fileRecordId: string) => {
    return request.delete<Res<FileDeleteRes>>({
        url: `/files/${fileRecordId}`,
        needPremission: true
    })
}

/**
 * @description 批量删除文件
 * @param {string[]} fileRecordIds 文件记录ID列表
 * @returns {Promise<Res<FileBatchDeleteRes>>} 删除成功的文件记录ID列表
 */
export const fileBatchDelete = (fileRecordIds: string[]) => {
    return request.delete<Res<FileBatchDeleteRes>>({
        url: '/files',
        data: fileRecordIds,
        needPremission: true
    })
}

// ==================== 文件下载 ====================
/**
 * @description 根据文件链接下载文件查询参数
 * @property {string} url 文件链接
 */
export interface FileDownloadByUrlReq {
    url: string
}

/**
 * @description 根据文件链接下载文件
 * @param {FileDownloadByUrlReq} params 查询参数（url）
 * @returns {Promise<Blob>} 文件流
 */
export const fileDownloadByUrl = (params: FileDownloadByUrlReq) => {
    return request.get<Blob>({
        url: '/files/url/',
        params,
        needPremission: true,
        needLoading: true,
        responseType: 'blob',
        /** 同参数并发合并为一次请求（Repeatimiter） */
        repeatLimit: 'coalesce'
    })
}

/**
 * @description 根据文件标识下载文件
 * @param {string} fileRecordId 文件记录ID
 * @returns {Promise<Blob>} 文件流
 */
export const fileDownload = (fileRecordId: string) => {
    return request.get<Blob>({
        url: `/files/${fileRecordId}/download`,
        needPremission: true,
        needLoading: true,
        responseType: 'blob',
        /** 同一文件并发下载合并为一次请求（Repeatimiter） */
        repeatLimit: 'coalesce'
    })
}
